// src/modules/assetManagement/assetApi.ts
// Customer Manager Library + Asset Management API. Backend: /api/v1/library (links, storage, upload log),
// /api/v1/assets/ingest (upload), /api/assets/share (guest share), /api/v1/tickets (support).
import { API_ROOT_URL, API_V1_BASE_URL } from "../../config/api";
import { ticketApi, type TicketCategory } from "../tickets/api";
import type {
  Asset,
  AssetFilters,
  AssetKind,
  AssetStatus,
  AttemptResolution,
  LibraryEvent,
  LibraryProduct,
  SupportTicket,
  UploadAttemptLog,
} from "./types";
import { ACCEPTED_EXTENSIONS, extensionOf } from "./utils";

const LIBRARY = `${API_V1_BASE_URL}/library`;

/** Shown wherever the UI explains how long a Glacier restore takes */
export const RESTORE_ESTIMATE = "3–5 hours";
/** While any asset is restoring, lists refresh this often to pick up finished restores */
const RESTORE_POLL_MS = 60_000;

export const CURRENT_USER = (() => {
  try {
    return (JSON.parse(localStorage.getItem("user") || "null")?.email as string) || "";
  } catch {
    return "";
  }
})();

// ---- change notifications ------------------------------------------------------
// Pages re-fetch after any mutation (see useAssetQuery).

const listeners = new Set<() => void>();
export const subscribeAssetChanges = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
const notify = () => listeners.forEach((fn) => fn());

let restorePoll: ReturnType<typeof setTimeout> | null = null;
const pollWhileRestoring = (assets: Asset[]) => {
  if (restorePoll || !assets.some((a) => a.storage === "restoring")) return;
  restorePoll = setTimeout(() => {
    restorePoll = null;
    notify();
  }, RESTORE_POLL_MS);
};

// ---- http ------------------------------------------------------------------------

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handleUnauthorized = () => {
  ["accessToken", "refreshToken", "user"].forEach((k) => localStorage.removeItem(k));
  window.location.assign("/login");
};

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { "Content-Type": "application/json", ...authHeaders(), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => null);
  if (res.status === 401) handleUnauthorized();
  if (!res.ok || body?.success === false) {
    const err = new Error(body?.message || body?.error || `Request failed (${res.status})`) as Error & { data?: unknown };
    err.data = body?.data;
    throw err;
  }
  return (body?.data ?? body) as T;
}

const post = <T,>(url: string, payload: unknown) => request<T>(url, { method: "POST", body: JSON.stringify(payload) });

const qs = (params: Record<string, string | string[] | undefined>) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    const value = Array.isArray(v) ? v.join(",") : v?.trim();
    if (value) q.set(k, value);
  });
  const s = q.toString();
  return s ? `?${s}` : "";
};

// ---- mapping ---------------------------------------------------------------------

const kindOf = (mime: string, filename: string): AssetKind => {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return ACCEPTED_EXTENSIONS[extensionOf(filename)] ?? "document";
};

const STATUS_MAP: Record<string, AssetStatus> = {
  completed: "completed",
  queued: "queued",
  received: "queued",
  validating: "queued",
  transcoding: "transcoding",
  failed: "failed",
  rejected: "rejected",
};

type ApiAsset = Omit<Asset, "kind" | "status"> & { status: string; fileCategory?: string | null };

const toAsset = (a: ApiAsset): Asset => ({
  ...a,
  kind: kindOf(a.mimeType || "", a.filename),
  status: STATUS_MAP[a.status] ?? "queued",
});

const toEvent = (e: LibraryEvent): LibraryEvent => ({ ...e, tags: (e.tags || []).filter(Boolean) });

// ---- support categories (labels in the UI -> ticket category codes) ---------------

const CATEGORY_CODES: Record<string, string> = {
  "Upload failed": "INGESTION_FAILURE",
  "Processing or playback problem": "CORRUPT_CONTENT",
  "Wrong details or links": "ASSET_ALLOCATION",
  "Archive or restore": "SYSTEM_ISSUE",
  "Sharing or guest access": "SYSTEM_ISSUE",
  "Something else": "OTHER",
};
let categoriesPromise: Promise<TicketCategory[]> | null = null;
const ticketCategories = () => {
  categoriesPromise ??= ticketApi.getCategories().catch((e) => {
    categoriesPromise = null;
    throw e;
  });
  return categoriesPromise;
};

/** Archive/restore report per-asset failures; surface them after refreshing */
const storageResult = async (call: Promise<{ done: string[]; failed: { id: string; error: string }[] }>, verb: string) => {
  try {
    const r = await call;
    if (r.failed.length) throw new Error(`${r.failed.length} asset(s) couldn't be ${verb}: ${r.failed[0].error}`);
    return true;
  } catch (e) {
    const data = (e as { data?: { failed?: { error: string }[] } }).data;
    if (data?.failed?.length) throw new Error(`${data.failed.length} asset(s) couldn't be ${verb}: ${data.failed[0].error}`);
    throw e;
  } finally {
    notify();
  }
};

// ---- upload ----------------------------------------------------------------------

export interface UploadHandle {
  promise: Promise<string>;
  abort: () => void;
}

export class UploadError extends Error {
  code: string;
  stage: "upload" | "processing";
  /** Set when the file was stored but processing failed: the asset exists with status failed */
  assetId?: string;
  constructor(message: string, code: string, stage: "upload" | "processing", assetId?: string) {
    super(message);
    this.code = code;
    this.stage = stage;
    this.assetId = assetId;
  }
}

/**
 * Upload one file to /assets/ingest. onProgress gets 0–100 for the bytes sent;
 * the server then stores and processes it before replying. Resolves to the asset id;
 * rejects with UploadError (assetId set) when the file was stored but processing failed.
 */
export const uploadFile = (file: File, onProgress: (pct: number) => void, onSent: () => void): UploadHandle => {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<string>((resolve, reject) => {
    const form = new FormData();
    form.append("files", file, file.name);
    xhr.open("POST", `${API_V1_BASE_URL}/assets/ingest`);
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.upload.onload = () => onSent();
    xhr.onerror = () => reject(new UploadError("Network error while sending the file", "NETWORK_ERROR", "upload"));
    xhr.ontimeout = () => reject(new UploadError("The upload timed out", "NETWORK_TIMEOUT", "upload"));
    xhr.onabort = () => reject(new UploadError("Upload cancelled", "CANCELLED", "upload"));
    xhr.onload = () => {
      let body: any = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = null;
      }
      if (xhr.status === 401) handleUnauthorized();
      const ok = xhr.status >= 200 && xhr.status < 300 && body?.success !== false;
      const stored = body?.data?.successful?.[0];
      const assetId = stored?.parentAssetId;
      if (ok && assetId && stored.processingFailed) {
        return reject(new UploadError(stored.processingError || "Processing failed", "TRANSCODE_FAILED", "processing", String(assetId)));
      }
      if (ok && assetId) return resolve(String(assetId));
      const message = body?.errors?.[0]?.error || body?.data?.failed?.[0]?.error || body?.message || `Upload failed (${xhr.status})`;
      const code = xhr.status === 413 ? "FILE_TOO_LARGE" : xhr.status >= 500 ? "SERVER_ERROR" : "PROCESSING_FAILED";
      reject(new UploadError(message, code, "processing"));
    };
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
};

// ---- api -------------------------------------------------------------------------

export const assetApi = {
  // ---- reads ---------------------------------------------------------------
  async listAssets(filters: AssetFilters = {}): Promise<Asset[]> {
    const rows = await request<ApiAsset[]>(
      `${LIBRARY}/assets${qs({
        search: filters.search,
        eventIds: filters.eventIds,
        productIds: filters.productIds,
        tags: filters.tags,
        statuses: filters.statuses,
        storages: filters.storages,
      })}`,
    );
    const assets = rows.map(toAsset);
    pollWhileRestoring(assets);
    return assets;
  },

  async getAsset(id: string): Promise<Asset | null> {
    try {
      const a = toAsset(await request<ApiAsset>(`${LIBRARY}/assets/${encodeURIComponent(id)}`));
      pollWhileRestoring([a]);
      return a;
    } catch (e) {
      if (/not found|invalid asset/i.test((e as Error).message)) return null;
      throw e;
    }
  },

  async listEvents(): Promise<LibraryEvent[]> {
    return (await request<LibraryEvent[]>(`${LIBRARY}/events`)).map(toEvent);
  },

  async listProducts(): Promise<LibraryProduct[]> {
    return request<LibraryProduct[]>(`${LIBRARY}/products`);
  },

  async listTags(): Promise<string[]> {
    return request<string[]>(`${LIBRARY}/tags`);
  },

  async listUploadLog(): Promise<UploadAttemptLog[]> {
    return request<UploadAttemptLog[]>(`${LIBRARY}/upload-log`);
  },

  // ---- links (Library + Asset Management) -----------------------------------
  /** Additive; the server adds an event for any product none of the asset's events contain */
  async linkAssets(assetIds: string[], eventIds: string[], productIds: string[], tags: string[] = []) {
    await post(`${LIBRARY}/assets/link`, { assetIds, eventIds, productIds, tags });
    notify();
    return true;
  },

  /** Products whose events were all unlinked are unlinked too */
  async unlinkAssets(assetIds: string[], eventIds: string[], productIds: string[]) {
    await post(`${LIBRARY}/assets/unlink`, { assetIds, eventIds, productIds });
    notify();
    return true;
  },

  // ---- Asset Management only -------------------------------------------------
  archiveAssets: (assetIds: string[]) => storageResult(post(`${LIBRARY}/assets/archive`, { assetIds }), "archived"),

  restoreAssets: (assetIds: string[]) => storageResult(post(`${LIBRARY}/assets/restore`, { assetIds }), "restored"),

  async shareAssets(assetIds: string[], email: string, expiresAt: string) {
    const r = await post<{ shareId: string; accessUrl: string }>(`${API_ROOT_URL}/assets/share`, {
      guestEmail: email,
      expiryDate: expiresAt,
      assetIds,
    });
    return { id: r.shareId, accessUrl: r.accessUrl, assetIds, email, expiresAt, createdAt: new Date().toISOString() };
  },

  async deleteAssets(assetIds: string[]) {
    await post(`${LIBRARY}/assets/delete`, { assetIds });
    notify();
    return true;
  },

  /** "Fix": upload the new file, move the old asset's links and tags to it, delete the old one. Resolves to the new id. */
  async replaceAssetFile(id: string, file: File): Promise<string> {
    const newId = await uploadFile(file, () => undefined, () => undefined).promise;
    await post(`${LIBRARY}/assets/${encodeURIComponent(id)}/replace`, { newAssetId: newId });
    notify();
    return newId;
  },

  // ---- first-line support -----------------------------------------------------
  async raiseSupportTicket(input: Omit<SupportTicket, "ref" | "createdAt">): Promise<SupportTicket> {
    const categories = await ticketCategories();
    const code = CATEGORY_CODES[input.category] ?? "OTHER";
    const category = categories.find((c) => c.code === code) ?? categories.find((c) => c.code === "OTHER") ?? categories[0];
    if (!category) throw new Error("No support categories are set up");
    const ticket = await ticketApi.raiseTicket({
      categoryId: category.id,
      subject: `${input.category}${input.fileName ? `: ${input.fileName}` : ""}`,
      description: input.description || input.category,
      raisedBy: CURRENT_USER,
      priority: input.priority,
      assetId: input.assetId || undefined,
    });
    return { ...input, ref: ticket.reference, createdAt: ticket.createdAt };
  },

  // ---- upload failure log -------------------------------------------------------
  async logUploadFailure(entry: Omit<UploadAttemptLog, "id" | "at" | "user" | "resolution">) {
    await post(`${LIBRARY}/upload-log`, entry);
    notify();
    return true;
  },

  /** Updates every open attempt for a file (e.g. after a retry succeeds or a ticket is raised) */
  async resolveFileAttempts(fileKey: string, resolution: AttemptResolution, ticketRef?: string) {
    await post(`${LIBRARY}/upload-log/resolve`, { fileKey, resolution, ticketRef });
    notify();
    return true;
  },

  async clearResolvedLog() {
    await post(`${LIBRARY}/upload-log/clear`, {});
    notify();
    return true;
  },
};